import React from 'react';
import { Loader2 } from 'lucide-react';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  icon,
  iconPosition = 'left',
  fullWidth = false,
  ...props
}) => {
  // Keep focus styling subtle (no thick black/colored rings) — app uses thin brand borders elsewhere.
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-slate-900 hover:bg-slate-800 text-white',
    secondary: 'bg-slate-800 hover:bg-slate-900 text-white',
    outline: 'border border-slate-900 text-slate-900 hover:bg-slate-50',
    ghost: 'text-slate-900 hover:bg-slate-50',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    destructive: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500', // Alias for danger
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    warning: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    black: 'bg-slate-900 hover:bg-slate-800 text-white'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg'
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`;
  
  const renderIcon = () => {
    if (loading) {
      return <Loader2 className="animate-spin h-4 w-4" />;
    }
    if (icon) {
      return icon;
    }
    return null;
  };
  
  const renderContent = () => {
    const iconElement = renderIcon();
    
    if (!iconElement) return children;
    
    if (iconPosition === 'left') {
      return (
        <>
          {iconElement}
          {children && <span className="ml-2">{children}</span>}
        </>
      );
    } else {
      return (
        <>
          {children && <span className="mr-2">{children}</span>}
          {iconElement}
        </>
      );
    }
  };
  
  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
